import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Trash2, ArrowUp, ArrowDown, GripVertical, Loader2, Save, Upload, Undo2,
  Pencil, Check, X as XIcon, Plus, GripHorizontal, AlertTriangle
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminGalleryManager
 *
 * Full CMS for the "Captured by Grace" gallery:
 *   • Sections (chapters): reorder (drag / arrows), rename inline, create, delete (with photo-move dialog)
 *   • Photos: thumbnails per chapter, delete-with-undo, upload, up/down arrow reorder,
 *     chapter-move dropdown, desktop drag-and-drop, batch "Save Order"
 *
 * Requires an `authHeader` prop returning a valid HTTP Basic header.
 */
const AdminGalleryManager = ({ authHeader }) => {
  const [chapters, setChapters] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [dragId, setDragId] = useState(null);
  const [sectionDragId, setSectionDragId] = useState(null);

  // Chapter renaming (in-place edit)
  const [editingChapterId, setEditingChapterId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');

  // Track original chapter titles/orders vs local so we know what to send on save
  const [chapterOriginals, setChapterOriginals] = useState({});

  // Upload
  const fileInputRef = useRef(null);
  const [uploadChapter, setUploadChapter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Undo-delete toast
  const [undoState, setUndoState] = useState(null); // { photo, remaining }
  const undoTimeoutRef = useRef(null);
  const undoIntervalRef = useRef(null);

  // Rich modals for section create / delete
  const [newSectionOpen, setNewSectionOpen] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [creatingSection, setCreatingSection] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null); // { chapter, photoCount, moveTargetId }
  const [deletingSection, setDeletingSection] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [chRes, phRes] = await Promise.all([
        axios.get(`${API}/gallery/chapters`),
        axios.get(`${API}/gallery/photos`)
      ]);
      const chs = (chRes.data || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      setChapters(chs);
      setPhotos(phRes.data || []);
      setChapterOriginals(Object.fromEntries(chs.map((c) => [c.id, { title: c.title, order: c.order }])));
      setDirty(false);
      // Ensure upload chapter is valid
      setUploadChapter((prev) => (chs.find((c) => c.id === prev) ? prev : chs[0]?.id || ''));
    } catch (err) {
      console.error('Load gallery failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markDirty = () => setDirty(true);

  // Group photos by chapter, ordered by `order` within each
  const grouped = useMemo(() => {
    const g = {};
    for (const c of chapters) g[c.id] = [];
    for (const p of photos) {
      if (!g[p.chapter]) g[p.chapter] = [];
      g[p.chapter].push(p);
    }
    for (const id of Object.keys(g)) {
      g[id].sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return g;
  }, [chapters, photos]);

  // ------- Photo controls -------
  const move = (photoId, delta) => {
    setPhotos((prev) => {
      const p = prev.find((x) => x.id === photoId);
      if (!p) return prev;
      const same = prev.filter((x) => x.chapter === p.chapter).sort((a, b) => (a.order || 0) - (b.order || 0));
      const idx = same.findIndex((x) => x.id === photoId);
      const t = idx + delta;
      if (t < 0 || t >= same.length) return prev;
      const a = same[idx], b = same[t];
      return prev.map((x) => {
        if (x.id === a.id) return { ...x, order: b.order };
        if (x.id === b.id) return { ...x, order: a.order };
        return x;
      });
    });
    markDirty();
  };

  const changeChapter = (photoId, newChapterId) => {
    setPhotos((prev) => {
      const target = prev.find((x) => x.id === photoId);
      if (!target || target.chapter === newChapterId) return prev;
      const destSize = prev.filter((x) => x.chapter === newChapterId).length;
      const updated = prev.map((x) => (x.id === photoId ? { ...x, chapter: newChapterId, order: destSize } : x));
      const oldChapter = target.chapter;
      const remaining = updated.filter((x) => x.chapter === oldChapter).sort((a, b) => (a.order || 0) - (b.order || 0));
      const reindex = new Map();
      remaining.forEach((x, i) => reindex.set(x.id, i));
      return updated.map((x) => (reindex.has(x.id) ? { ...x, order: reindex.get(x.id) } : x));
    });
    markDirty();
  };

  // ------- Delete + Undo -------
  const clearUndoTimers = () => {
    if (undoTimeoutRef.current) { clearTimeout(undoTimeoutRef.current); undoTimeoutRef.current = null; }
    if (undoIntervalRef.current) { clearInterval(undoIntervalRef.current); undoIntervalRef.current = null; }
  };

  const remove = async (photo) => {
    if (!window.confirm(`Are you sure you want to delete this photo?\n\n"${photo.alt}"\n\nYou'll have 5 seconds to undo.`)) return;
    try {
      await axios.delete(`${API}/admin/gallery/photos/${photo.id}`, { headers: authHeader() });
      setPhotos((prev) => prev.filter((x) => x.id !== photo.id));
      clearUndoTimers();
      setUndoState({ photo, remaining: 5 });
      undoIntervalRef.current = setInterval(() => {
        setUndoState((s) => (s ? { ...s, remaining: Math.max(0, s.remaining - 1) } : s));
      }, 1000);
      undoTimeoutRef.current = setTimeout(() => { clearUndoTimers(); setUndoState(null); }, 5000);
    } catch (err) {
      console.error(err);
      alert('Failed to delete photo. Please try again.');
    }
  };

  const undoDelete = async () => {
    const state = undoState;
    if (!state) return;
    clearUndoTimers();
    setUndoState(null);
    try {
      const p = state.photo;
      const res = await axios.post(
        `${API}/admin/gallery/restore`,
        { id: p.id, filename: p.filename, url: p.url, alt: p.alt, chapter: p.chapter, order: p.order || 0 },
        { headers: authHeader() },
      );
      const restored = res.data?.photo || state.photo;
      setPhotos((prev) => (prev.find((x) => x.id === restored.id) ? prev : [...prev, restored]));
      setSavedMsg('Photo restored.');
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (err) {
      console.error(err);
      alert('Failed to restore the photo. Please refresh and try again.');
    }
  };

  useEffect(() => () => clearUndoTimers(), []);

  // ------- Upload -------
  const onPickFiles = () => fileInputRef.current?.click();

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (dirty) {
      const ok = window.confirm('You have unsaved changes. Uploading will refresh the list and discard them. Continue?');
      if (!ok) return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const form = new FormData();
      form.append('chapter', uploadChapter);
      files.forEach((f) => form.append('files', f));
      await axios.post(`${API}/admin/gallery/upload`, form, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => { if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100)); },
      });
      setSavedMsg(`Uploaded ${files.length} photo${files.length > 1 ? 's' : ''}.`);
      setTimeout(() => setSavedMsg(''), 3000);
      await load();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ------- Section (chapter) controls -------
  const moveSection = (chapterId, delta) => {
    setChapters((prev) => {
      const sorted = [...prev].sort((a, b) => (a.order || 0) - (b.order || 0));
      const idx = sorted.findIndex((c) => c.id === chapterId);
      const t = idx + delta;
      if (idx < 0 || t < 0 || t >= sorted.length) return prev;
      // Swap orders
      const a = sorted[idx], b = sorted[t];
      return prev.map((c) => {
        if (c.id === a.id) return { ...c, order: b.order };
        if (c.id === b.id) return { ...c, order: a.order };
        return c;
      });
    });
    markDirty();
  };

  const startRename = (chapter) => {
    setEditingChapterId(chapter.id);
    setEditingTitle(chapter.title);
  };
  const cancelRename = () => { setEditingChapterId(null); setEditingTitle(''); };
  const commitRename = () => {
    const newTitle = editingTitle.trim();
    if (!newTitle || !editingChapterId) { cancelRename(); return; }
    setChapters((prev) => prev.map((c) => (c.id === editingChapterId ? { ...c, title: newTitle } : c)));
    markDirty();
    cancelRename();
  };

  const openNewSection = () => {
    setNewSectionTitle('');
    setNewSectionOpen(true);
  };

  const submitNewSection = async () => {
    const title = newSectionTitle.trim();
    if (!title) return;
    setCreatingSection(true);
    try {
      const res = await axios.post(
        `${API}/admin/gallery/chapters`,
        { title },
        { headers: authHeader() },
      );
      const doc = res.data;
      setChapters((prev) => [...prev, doc].sort((a, b) => (a.order || 0) - (b.order || 0)));
      setChapterOriginals((prev) => ({ ...prev, [doc.id]: { title: doc.title, order: doc.order } }));
      setSavedMsg(`Section "${doc.title}" created.`);
      setTimeout(() => setSavedMsg(''), 2500);
      setNewSectionOpen(false);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.detail || 'Could not create section. Please try again.');
    } finally {
      setCreatingSection(false);
    }
  };

  const openDeleteSection = (chapter) => {
    const count = (grouped[chapter.id] || []).length;
    const others = chapters.filter((c) => c.id !== chapter.id);
    setDeleteModal({
      chapter,
      photoCount: count,
      moveTargetId: count > 0 && others[0] ? others[0].id : '',
    });
  };

  const confirmDeleteSection = async () => {
    if (!deleteModal) return;
    const { chapter, photoCount, moveTargetId } = deleteModal;
    setDeletingSection(true);
    try {
      let msg;
      if (photoCount > 0) {
        if (!moveTargetId) {
          alert('Please choose a section to move photos to.');
          setDeletingSection(false);
          return;
        }
        await axios.delete(
          `${API}/admin/gallery/chapters/${encodeURIComponent(chapter.id)}?move_photos_to=${encodeURIComponent(moveTargetId)}`,
          { headers: authHeader() },
        );
        const targetTitle = chapters.find((c) => c.id === moveTargetId)?.title || 'the other section';
        msg = `Deleted "${chapter.title}"; moved ${photoCount} photo${photoCount > 1 ? 's' : ''} to "${targetTitle}".`;
      } else {
        await axios.delete(
          `${API}/admin/gallery/chapters/${encodeURIComponent(chapter.id)}`,
          { headers: authHeader() },
        );
        msg = `Section "${chapter.title}" deleted.`;
      }
      setDeleteModal(null);
      setSavedMsg(msg);
      setTimeout(() => setSavedMsg(''), 3500);
      await load();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.detail || 'Could not delete section.');
    } finally {
      setDeletingSection(false);
    }
  };

  // ------- Save all pending changes -------
  const save = async () => {
    setSaving(true);
    try {
      // 1) Save photo orders/chapters
      const byChap = {};
      for (const p of photos) (byChap[p.chapter] = byChap[p.chapter] || []).push(p);
      const photoItems = [];
      for (const chId of Object.keys(byChap)) {
        byChap[chId].sort((a, b) => (a.order || 0) - (b.order || 0))
          .forEach((p, i) => photoItems.push({ id: p.id, chapter: chId, order: i }));
      }
      if (photoItems.length) {
        await axios.patch(`${API}/admin/gallery/reorder`, { items: photoItems }, { headers: authHeader() });
      }

      // 2) Save chapter order changes (normalise 0..n-1)
      const sortedCh = [...chapters].sort((a, b) => (a.order || 0) - (b.order || 0));
      const chapterItems = sortedCh.map((c, i) => ({ id: c.id, order: i }));
      if (chapterItems.length) {
        await axios.patch(`${API}/admin/gallery/chapters/reorder`, { items: chapterItems }, { headers: authHeader() });
      }

      // 3) Save chapter title changes (only those that differ from originals)
      const titleUpdates = chapters.filter((c) => (chapterOriginals[c.id]?.title || '') !== c.title);
      for (const c of titleUpdates) {
        await axios.patch(
          `${API}/admin/gallery/chapters/${encodeURIComponent(c.id)}`,
          { title: c.title },
          { headers: authHeader() },
        );
      }

      setSavedMsg('Saved!');
      setTimeout(() => setSavedMsg(''), 2500);
      await load();
    } catch (err) {
      console.error(err);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ------- Native HTML5 drag/drop -------
  // Photos
  const onDragStart = (e, photoId) => {
    setDragId(photoId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', photoId);
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDropOnPhoto = (e, targetPhoto) => {
    e.preventDefault();
    e.stopPropagation();

    // If a whole section is being dragged, delegate to section reorder so drops
    // on photo tiles (which cover most of a section's area) still work.
    if (sectionDragId) {
      if (sectionDragId !== targetPhoto.chapter) {
        setChapters((prev) => {
          const sorted = [...prev].sort((a, b) => (a.order || 0) - (b.order || 0));
          const srcIdx = sorted.findIndex((c) => c.id === sectionDragId);
          const tgtIdx = sorted.findIndex((c) => c.id === targetPhoto.chapter);
          if (srcIdx < 0 || tgtIdx < 0) return prev;
          const [moved] = sorted.splice(srcIdx, 1);
          sorted.splice(tgtIdx, 0, moved);
          const orderMap = new Map();
          sorted.forEach((c, i) => orderMap.set(c.id, i));
          return prev.map((c) => ({ ...c, order: orderMap.get(c.id) ?? c.order }));
        });
        markDirty();
      }
      setSectionDragId(null);
      return;
    }

    const sourceId = dragId || e.dataTransfer.getData('text/plain');
    if (!sourceId || sourceId === targetPhoto.id) return;
    setPhotos((prev) => {
      const source = prev.find((x) => x.id === sourceId);
      const target = prev.find((x) => x.id === targetPhoto.id);
      if (!source || !target) return prev;
      const list = prev.filter((x) => x.id !== source.id);
      const destSorted = list.filter((x) => x.chapter === target.chapter).sort((a, b) => (a.order || 0) - (b.order || 0));
      const targetIndex = destSorted.findIndex((x) => x.id === target.id);
      destSorted.splice(targetIndex, 0, { ...source, chapter: target.chapter });
      const destMap = new Map();
      destSorted.forEach((x, i) => destMap.set(x.id, i));
      const srcList = list.filter((x) => x.chapter === source.chapter && x.chapter !== target.chapter)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const srcMap = new Map();
      srcList.forEach((x, i) => srcMap.set(x.id, i));
      const result = list.map((x) => {
        if (destMap.has(x.id)) return { ...x, chapter: target.chapter, order: destMap.get(x.id) };
        if (srcMap.has(x.id)) return { ...x, order: srcMap.get(x.id) };
        return x;
      });
      if (!result.find((x) => x.id === source.id)) {
        result.push({ ...source, chapter: target.chapter, order: destMap.get(source.id) ?? 0 });
      }
      return result;
    });
    markDirty();
    setDragId(null);
  };
  const onDropOnChapter = (e, chapterId) => {
    e.preventDefault();
    // If a whole SECTION is being dragged, reorder sections instead of photos
    if (sectionDragId) {
      if (sectionDragId !== chapterId) {
        setChapters((prev) => {
          const sorted = [...prev].sort((a, b) => (a.order || 0) - (b.order || 0));
          const srcIdx = sorted.findIndex((c) => c.id === sectionDragId);
          const tgtIdx = sorted.findIndex((c) => c.id === chapterId);
          if (srcIdx < 0 || tgtIdx < 0) return prev;
          const [moved] = sorted.splice(srcIdx, 1);
          sorted.splice(tgtIdx, 0, moved);
          const orderMap = new Map();
          sorted.forEach((c, i) => orderMap.set(c.id, i));
          return prev.map((c) => ({ ...c, order: orderMap.get(c.id) ?? c.order }));
        });
        markDirty();
      }
      setSectionDragId(null);
      return;
    }

    const sourceId = dragId || e.dataTransfer.getData('text/plain');
    if (!sourceId) return;
    setPhotos((prev) => {
      const source = prev.find((x) => x.id === sourceId);
      if (!source || source.chapter === chapterId) return prev;
      const destCount = prev.filter((x) => x.chapter === chapterId).length;
      const list = prev.map((x) => (x.id === source.id ? { ...x, chapter: chapterId, order: destCount } : x));
      const oldChapter = source.chapter;
      const remain = list.filter((x) => x.chapter === oldChapter).sort((a, b) => (a.order || 0) - (b.order || 0));
      const idxMap = new Map();
      remain.forEach((x, i) => idxMap.set(x.id, i));
      return list.map((x) => (idxMap.has(x.id) ? { ...x, order: idxMap.get(x.id) } : x));
    });
    markDirty();
    setDragId(null);
  };

  // Sections (whole-chapter drag)
  const onSectionDragStart = (e, chapterId) => {
    setSectionDragId(chapterId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-section', chapterId);
  };
  const onSectionDragEnd = () => setSectionDragId(null);

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 text-center text-[#5A4A4A]">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#C8A951]" />
        Loading gallery…
      </div>
    );
  }

  const sortedChapters = [...chapters].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-6" data-testid="admin-gallery-manager">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onFilesSelected}
        className="hidden"
        data-testid="upload-file-input"
      />

      {/* Sticky action bar */}
      <div className="sticky top-2 z-10 bg-white rounded-xl shadow-sm border border-[#E5E0D8] px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="text-[#5A4A4A] text-sm">
          <strong className="text-[#3B2F2F]">{photos.length}</strong> photos across{' '}
          <strong className="text-[#3B2F2F]">{chapters.length}</strong> sections
          {dirty && <span className="ml-3 text-[#C8A951]">• unsaved changes</span>}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {savedMsg && <span className="text-green-600 text-sm">{savedMsg}</span>}

          <button
            type="button"
            onClick={openNewSection}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#3B2F2F] text-[#3B2F2F] text-sm font-medium hover:bg-[#F8F5EF] transition-colors"
            data-testid="new-section-btn"
          >
            <Plus className="w-4 h-4" />
            New Section
          </button>

          <label className="text-[#5A4A4A] text-sm">
            Upload to:{' '}
            <select
              value={uploadChapter}
              onChange={(e) => setUploadChapter(e.target.value)}
              className="ml-1 border border-[#E5E0D8] rounded px-2 py-1 text-sm text-[#3B2F2F] focus:outline-none focus:border-[#C8A951]"
              data-testid="upload-chapter-select"
              disabled={uploading}
            >
              {sortedChapters.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={onPickFiles}
            disabled={uploading || !uploadChapter}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#3B2F2F] text-white text-sm font-medium hover:bg-[#2C221F] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="upload-photos-btn"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Uploading {uploadProgress ? `${uploadProgress}%` : '…'}</>
            ) : (
              <><Upload className="w-4 h-4" />Upload Photos</>
            )}
          </button>

          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#C8A951] text-white text-sm font-medium hover:bg-[#B09240] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="save-order-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Undo toast */}
      {undoState && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#3B2F2F] text-white rounded-full shadow-lg px-5 py-3 flex items-center gap-4"
          role="status"
          data-testid="undo-toast"
        >
          <span className="text-sm">
            Photo deleted <span className="opacity-60">· auto-removes in {undoState.remaining}s</span>
          </span>
          <button
            type="button"
            onClick={undoDelete}
            className="inline-flex items-center gap-1 bg-[#C8A951] hover:bg-[#B09240] text-white text-sm font-medium px-3 py-1.5 rounded-full transition-colors"
            data-testid="undo-delete-btn"
          >
            <Undo2 className="w-4 h-4" />
            Undo
          </button>
        </div>
      )}

      {sortedChapters.map((chapter, chapterIdx) => {
        const isEditing = editingChapterId === chapter.id;
        const isDragging = sectionDragId === chapter.id;
        return (
          <section
            key={chapter.id}
            className={`bg-white rounded-xl border border-[#E5E0D8] p-4 transition-shadow ${
              isDragging ? 'opacity-40' : ''
            }`}
            onDragOver={onDragOver}
            onDrop={(e) => onDropOnChapter(e, chapter.id)}
            data-testid={`chapter-${chapter.id}`}
          >
            <header className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                {/* Section drag handle */}
                <span
                  draggable
                  onDragStart={(e) => onSectionDragStart(e, chapter.id)}
                  onDragEnd={onSectionDragEnd}
                  className="cursor-grab active:cursor-grabbing text-[#5A4A4A] hover:text-[#3B2F2F] p-1"
                  title="Drag to reorder section"
                  aria-label="Drag section"
                  data-testid={`section-drag-${chapter.id}`}
                >
                  <GripHorizontal className="w-5 h-5" />
                </span>

                {isEditing ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename();
                        if (e.key === 'Escape') cancelRename();
                      }}
                      className="font-serif text-lg text-[#3B2F2F] border-b border-[#C8A951] focus:outline-none px-1 min-w-[220px]"
                      autoFocus
                      data-testid={`chapter-title-input-${chapter.id}`}
                    />
                    <button type="button" onClick={commitRename} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Save name" data-testid={`chapter-save-title-${chapter.id}`}>
                      <Check className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={cancelRename} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Cancel">
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <h3 className="font-serif text-lg text-[#3B2F2F] flex items-center gap-2 min-w-0" data-testid={`chapter-title-${chapter.id}`}>
                    <span className="truncate">{chapter.title}</span>
                    <span className="text-[#5A4A4A] text-sm font-sans flex-shrink-0">
                      ({(grouped[chapter.id] || []).length})
                    </span>
                  </h3>
                )}

                {!isEditing && (
                  <button
                    type="button"
                    onClick={() => startRename(chapter)}
                    className="p-1.5 text-[#5A4A4A] hover:text-[#3B2F2F] hover:bg-[#F8F5EF] rounded"
                    title="Rename section"
                    data-testid={`rename-section-${chapter.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveSection(chapter.id, -1)}
                  disabled={chapterIdx === 0}
                  className="p-1.5 text-[#5A4A4A] hover:text-[#3B2F2F] hover:bg-[#F8F5EF] rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move section up"
                  data-testid={`section-up-${chapter.id}`}
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveSection(chapter.id, 1)}
                  disabled={chapterIdx === sortedChapters.length - 1}
                  className="p-1.5 text-[#5A4A4A] hover:text-[#3B2F2F] hover:bg-[#F8F5EF] rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move section down"
                  data-testid={`section-down-${chapter.id}`}
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => openDeleteSection(chapter)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                  title="Delete section"
                  data-testid={`delete-section-${chapter.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </header>

            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {(grouped[chapter.id] || []).map((photo, i, arr) => (
                <div
                  key={photo.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, photo.id)}
                  onDragEnd={() => setDragId(null)}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDropOnPhoto(e, photo)}
                  className={`relative group border rounded-lg overflow-hidden bg-[#F8F5EF] transition-shadow ${
                    dragId === photo.id ? 'opacity-40' : 'hover:shadow-md'
                  }`}
                  data-testid={`admin-photo-${photo.id}`}
                >
                  <div className="aspect-square">
                    <img
                      src={photo.url}
                      alt={photo.alt}
                      loading="lazy"
                      className="w-full h-full object-cover pointer-events-none"
                    />
                  </div>

                  <span className="absolute top-1 left-1 bg-black/40 backdrop-blur-sm text-white p-1 rounded pointer-events-none opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" aria-hidden="true">
                    <GripVertical className="w-3 h-3" />
                  </span>

                  <button
                    type="button"
                    onClick={() => remove(photo)}
                    className="absolute top-1 right-1 bg-red-600/90 hover:bg-red-700 text-white p-1.5 rounded opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    title="Delete photo"
                    data-testid={`delete-photo-${photo.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>

                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(photo.id, -1)}
                      disabled={i === 0}
                      className="text-white p-1 rounded hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                      data-testid={`move-up-${photo.id}`}
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(photo.id, 1)}
                      disabled={i === arr.length - 1}
                      className="text-white p-1 rounded hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                      data-testid={`move-down-${photo.id}`}
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <select
                      value={photo.chapter}
                      onChange={(e) => changeChapter(photo.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="ml-auto bg-black/70 text-white text-[11px] rounded px-1.5 py-0.5 border border-white/20 max-w-[140px] truncate"
                      title="Move to section"
                      data-testid={`chapter-select-${photo.id}`}
                    >
                      {sortedChapters.map((c) => (
                        <option key={c.id} value={c.id}>{c.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
              {(grouped[chapter.id] || []).length === 0 && (
                <div className="col-span-full text-center text-[#5A4A4A] text-sm py-6 italic border-2 border-dashed border-[#E5E0D8] rounded">
                  No photos in this section yet. Drag a photo here or upload some.
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* --- New Section dialog --- */}
      <Dialog open={newSectionOpen} onOpenChange={(v) => !creatingSection && setNewSectionOpen(v)}>
        <DialogContent className="sm:max-w-[440px]" data-testid="new-section-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#3B2F2F]">Create a new section</DialogTitle>
            <DialogDescription>
              Sections group photos in your gallery. Give the new section a name — you can reorder or rename it later.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label htmlFor="new-section-title" className="text-sm text-[#5A4A4A] mb-1.5 block">
              Section name
            </label>
            <Input
              id="new-section-title"
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newSectionTitle.trim() && !creatingSection) submitNewSection();
              }}
              placeholder="e.g. Wedding Party Portraits"
              autoFocus
              data-testid="new-section-title-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setNewSectionOpen(false)}
              disabled={creatingSection}
              data-testid="new-section-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitNewSection}
              disabled={!newSectionTitle.trim() || creatingSection}
              className="bg-[#3B2F2F] hover:bg-[#2C221F] text-white"
              data-testid="new-section-submit"
            >
              {creatingSection ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Section
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Delete Section dialog --- */}
      <Dialog open={!!deleteModal} onOpenChange={(v) => !deletingSection && !v && setDeleteModal(null)}>
        {deleteModal && (
          <DialogContent className="sm:max-w-[500px]" data-testid="delete-section-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-[#3B2F2F] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Delete &ldquo;{deleteModal.chapter.title}&rdquo;?
              </DialogTitle>
              <DialogDescription>
                {deleteModal.photoCount > 0 ? (
                  <>
                    This section contains <strong className="text-[#3B2F2F]">{deleteModal.photoCount} photo{deleteModal.photoCount > 1 ? 's' : ''}</strong>.
                    To protect them, choose another section to move them into first.
                  </>
                ) : (
                  <>This section has no photos, so it can be deleted safely. This action can&rsquo;t be undone.</>
                )}
              </DialogDescription>
            </DialogHeader>

            {deleteModal.photoCount > 0 && (
              <div className="py-2">
                <label className="text-sm text-[#5A4A4A] mb-1.5 block">
                  Move photos to
                </label>
                <Select
                  value={deleteModal.moveTargetId}
                  onValueChange={(v) => setDeleteModal((s) => ({ ...s, moveTargetId: v }))}
                >
                  <SelectTrigger data-testid="delete-section-move-target">
                    <SelectValue placeholder="Choose a destination section" />
                  </SelectTrigger>
                  <SelectContent>
                    {chapters
                      .filter((c) => c.id !== deleteModal.chapter.id)
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`delete-section-target-${c.id}`}>
                          {c.title} <span className="text-xs text-[#5A4A4A]">({(grouped[c.id] || []).length})</span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteModal(null)}
                disabled={deletingSection}
                data-testid="delete-section-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmDeleteSection}
                disabled={deletingSection || (deleteModal.photoCount > 0 && !deleteModal.moveTargetId)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-section-confirm"
              >
                {deletingSection ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {deleteModal.photoCount > 0 ? 'Move & Delete' : 'Delete Section'}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

export default AdminGalleryManager;
